/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FC, useEffect, useRef, useState } from 'react';
import { useNavigate, useHref, useLinkClickHandler } from 'react-router-dom';
import {
  Button,
  ButtonWC,
  Icon,
  Dropdown,
  DropdownWC,
  Stack,
  Typeahead,
  Divider,
  Spacer,
  Menu,
  MenuItem,
  TextSubtitle,
} from '@pypestream/design-system';

import {
  ProStudioPages,
  useCurrentRoute,
  useProStudioRoutes,
} from '../../hooks';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../xstate/app.xstate';

interface SolutionItem {
  id?: string;
  name?: string;
}

export const SolutionsDropdown: FC = () => {
  const { solutions, projectId, selectedSolution } = useProStudioCtxSelector(
    (ctx) => ({
      projectId: ctx.project?.id,
      selectedSolution: ctx.selectedBot,
      solutions: (ctx.solutions || [])
        .map((bot) => ({
          id: bot.id,
          name: bot.displayName,
        }))
        // @todo - move this sorting to gql query, currently we don't have ability to add sorting to the getSolutions query so doing it here
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    })
  );

  const [activeSolution, setActiveSolution] = useState<SolutionItem | null>();

  const [filteredOptions, setFilteredOptions] =
    useState<SolutionItem[]>(solutions);
  const [value, setValue] = useState<string>('');

  const dropdownRef = useRef<DropdownWC | null>(null);

  const navigate = useNavigate();
  const routes = useProStudioRoutes();

  const currentRoute = useCurrentRoute();
  const isHomePage = currentRoute.label === routes.home.label;

  const homepageHref = useHref(routes.home.path);
  const handleHomepageClick = useLinkClickHandler<ButtonWC>(routes.home.path);

  const handleTypeheadInput = (event: CustomEvent) => {
    setFilteredOptions(event.detail.filtered);
    setValue(event.detail.value);
  };

  useEffect(() => {
    if (!solutions) return;
    setFilteredOptions(solutions);
  }, [solutions]);

  useEffect(() => {
    if (!selectedSolution || selectedSolution.uniqueId === activeSolution?.id)
      return;

    if (isHomePage) {
      setActiveSolution(null);
      return;
    }

    setActiveSolution({
      id: selectedSolution?.uniqueId,
      name: selectedSolution?.displayName,
    });
  }, [selectedSolution, activeSolution, isHomePage]);

  if (!projectId) return;

  const allSolutionsText = 'All Solutions';

  return (
    <>
      <Button
        test-id="ps-solutions-dropdown-trigger"
        id="solutions-selector-trigger"
        slot="nav-breadcrumb-extra"
        variant="ghost"
        size="xlarge"
        className="c-primary-nav__solutions-button"
        title={activeSolution?.name || undefined}
      >
        <span className="c-primary-nav__solutions-button-text">
          {isHomePage
            ? allSolutionsText
            : activeSolution?.name || allSolutionsText}
        </span>
        <Icon name="chevrons-up-down" size="xsmall" slot="suffix" />
      </Button>
      <Dropdown
        // @ts-ignore
        ref={dropdownRef}
        trigger="solutions-selector-trigger"
        placement="top-start"
        test-id="ps-solutions-dropdown"
      >
        <div className="c-primary-nav__solutions-dropdown">
          <div className="c-primary-nav__solutions-search">
            <Stack gutter="2xsmall" nowrap>
              <Typeahead
                size="medium"
                searchKeys={['name']}
                value={value}
                data={solutions as SolutionItem[]}
                onInput={handleTypeheadInput}
              />
              <Button
                test-id="nav-link-solutions"
                variant="validation-neutral"
                href={homepageHref}
                onClick={(event: React.MouseEvent<ButtonWC, MouseEvent>) => {
                  setActiveSolution(null);
                  dropdownRef.current?.hide();
                  handleHomepageClick(event);
                }}
                style={{ flexShrink: 0 }}
              >
                {allSolutionsText}
              </Button>
            </Stack>
            <Spacer size="2xsmall" />
            <Divider />
          </div>
          <div className="c-primary-nav__solutions-list">
            <Spacer size="2xsmall" />
            <Menu>
              {filteredOptions?.map((item) => (
                <MenuItem
                  key={item.id}
                  value={item.id}
                  selected={item.id === activeSolution?.id}
                  onClick={() => {
                    if (!item.id || !item.name) return;
                    setActiveSolution(item);
                    sendProStudioEvent({
                      type: 'selectBot',
                      name: item.name,
                      id: item.id,
                      cb: (path?: string) => {
                        const currentPage =
                          currentRoute.route ===
                            ProStudioPages.CHANNELS.toLowerCase() ||
                          currentRoute.route ===
                            ProStudioPages.FILES.toLowerCase() ||
                          currentRoute.route ===
                            ProStudioPages.WORKFLOWS.toLowerCase() ||
                          currentRoute.route ===
                            ProStudioPages.ACTIONS.toLowerCase()
                            ? `/${currentRoute.route}`
                            : '';

                        const newPath = `${path}${currentPage}`;
                        navigate(newPath || '');
                      },
                    });
                  }}
                >
                  {item.name}
                </MenuItem>
              ))}
            </Menu>
            {!filteredOptions?.length && (
              <>
                <Spacer size="xsmall" />
                <TextSubtitle
                  size="xsmall"
                  variant="tertiary"
                  textAlign="center"
                >
                  No solutions found
                </TextSubtitle>
              </>
            )}
          </div>
        </div>
      </Dropdown>
    </>
  );
};
